import { SOUND_REACTION_IDS, type SoundReactionId } from '@duck/contracts';

/**
 * Soundboard audio (M7a "Sound reactions", emotion: Joy / Belonging).
 *
 * The Discord-soundboard cousin of the floating emoji {@link import('@duck/contracts').REACTIONS}: a
 * short, fun sound the whole room HEARS together. Every catalog sound is a real MP3 served from the web
 * app's `public/sounds/<id>.mp3` (the `id` IS the asset slug). The wire only ever carries the validated
 * `soundId` — the closed {@link SOUND_REACTION_IDS} catalog is the single gate on what can play, so a
 * forged packet can never make the room fetch an arbitrary URL.
 *
 * Clips are fetched + decoded LAZILY on first use and the decoded {@link AudioBuffer} is cached, so a
 * shared moment is gapless after the first play and the cold cost is one small download. Everything is
 * routed through one shared AudioContext so the Sound Director's safety rails still hold. Unlike the old
 * procedural blips these are real recorded memes of VARIABLE length (a few are several seconds), so the
 * "never a wall of noise" promise now rests on three layers, not on clip brevity:
 *   • LIMITED CONCURRENCY — at most {@link MAX_CONCURRENT} clips ring at once; extra triggers are dropped
 *     (never queued into a pile-up), and a tiny per-sound debounce coalesces double-fires.
 *   • SERVER RATE-LIMIT — the realtime caps SOUND_REACT per sender (the shared emote limiter), and the
 *     per-listener BLOCK suppresses a griefer's audio entirely on the blocker's client.
 *   • GENTLE CEILING — a master gain feeds a brick-wall limiter (DynamicsCompressor) before the speakers,
 *     so even several overlapping full-scale clips stay comfortable rather than clipping into harshness.
 *   • RESPECTS THE SYSTEM — a single shared, lazily-created AudioContext, resumed on a user gesture
 *     (autoplay policy), and never created during SSR / in a non-DOM environment.
 *
 * Nothing here is persisted and nothing affects progression — it's ephemeral, shared delight.
 */

/** Master gain feeding the limiter. Recorded clips are already full-scale, so this sits near unity and
 *  the limiter (not a low ceiling) is what tames overlaps. This is the channel CEILING (the "100%"
 *  point); the player's effects-volume preference scales BELOW it via {@link setSoundboardVolume}. */
const MASTER_GAIN = 0.9;

/** The player's effects-volume preference as a 0..1 multiplier on {@link MASTER_GAIN} (1 = full). Kept at
 *  module scope so it survives the lazy (re)creation of the AudioContext — a level set before any sound
 *  has played still applies to the first clip. Driven by the audio-mix store via {@link setSoundboardVolume}. */
let sfxVolume = 1;
/** Most clips a soundboard sound can ring at once before extra triggers are dropped (no pile-ups). Kept
 *  low because recorded clips are longer/louder than the old ≤1.2s synth blips, so fewer can overlap. */
const MAX_CONCURRENT = 3;
/** Per-sound debounce (ms): coalesces an accidental double-fire of the SAME sound into one play. */
const DEBOUNCE_MS = 120;
/** Base path the app is served from (usually "/"), so `/sounds/*.mp3` resolves under any deploy prefix. */
const BASE_URL = import.meta.env.BASE_URL ?? '/';

/** Monotonic wall clock (ms) for the debounce — decoupled from `AudioContext.currentTime`, which freezes
 *  while the context is suspended (e.g. a backgrounded tab) and would skew the human-perceived window. */
function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/** A minimal structural view of the Web Audio constructor (incl. the webkit-prefixed legacy name). */
type AudioContextCtor = new () => AudioContext;

let ctx: AudioContext | null = null;
/** The node every clip connects to: master gain → limiter → destination. */
let master: GainNode | null = null;
/** How many clips are currently sounding — caps concurrency so a burst can't become a roar. */
let voices = 0;
/** Last play time per soundId, for the tiny same-sound debounce. */
const lastPlayedAt = new Map<SoundReactionId, number>();
/** Decoded clips, cached after first fetch so repeat plays are gapless. */
const bufferCache = new Map<SoundReactionId, AudioBuffer>();
/** In-flight fetch+decode per id, so concurrent requests for the same sound share one download. */
const inflight = new Map<SoundReactionId, Promise<AudioBuffer | null>>();
/** Ids whose clip is permanently missing (a 404 — e.g. a catalog/asset drift). Negatively cached so a
 *  dead id becomes a single silent no-op instead of re-fetching on every hover/click. Transient errors
 *  (network blip / 5xx / decode hiccup) are NOT cached here, so they can still retry later. */
const missingIds = new Set<SoundReactionId>();

// ── Dev/e2e observability (read by OfficePage's `window.__duckSound()` hook) ──
/** The id of the most recently PLAYED sound (after the autoplay/concurrency gates), or null. */
let lastPlayed: SoundReactionId | null = null;
/** Monotonic count of sounds actually played (post-gate) — lets the e2e assert a play happened. */
let playedCount = 0;

/** Resolve the Web Audio constructor in a DOM-safe way (returns null in SSR / unsupported browsers). */
function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Lazily create (once) the shared AudioContext + master gain → limiter → destination chain. Returns null
 * when Web Audio is unavailable (SSR / very old browser) so callers no-op gracefully — a missing
 * soundboard never throws.
 */
function ensureContext(): { ctx: AudioContext; master: GainNode } | null {
  if (ctx && master) return { ctx, master };
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  try {
    const created = new Ctor();
    const gain = created.createGain();
    gain.gain.value = MASTER_GAIN * sfxVolume;
    // Brick-wall-ish limiter so overlapping full-scale clips never clip into harshness.
    const limiter = created.createDynamicsCompressor();
    limiter.threshold.value = -8;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    gain.connect(limiter).connect(created.destination);
    ctx = created;
    master = gain;
    return { ctx, master };
  } catch {
    // Some browsers throw if too many AudioContexts exist; degrade silently (audio is a bonus, never required).
    return null;
  }
}

/**
 * Resume the AudioContext from a user gesture (autoplay policy). Safe to call repeatedly; a no-op if
 * already running or unavailable. OfficePage calls this off the same click that opens the soundboard /
 * fires a sound, so the FIRST sound a player triggers is audible without a "click to enable" step.
 */
export function unlockSoundAudio(): void {
  const audio = ensureContext();
  if (audio && audio.ctx.state === 'suspended') void audio.ctx.resume();
}

/**
 * Set the effects channel volume — a 0..1 multiplier on {@link MASTER_GAIN} (0 = silent, 1 = full).
 * Driven by the per-device audio mix (the "Sound effects" fader). Remembered at module scope so it
 * applies even before the AudioContext exists; if the bus is already live we ramp (no zipper click).
 * Never creates the context itself (so adjusting the slider with audio still locked stays a silent no-op).
 */
export function setSoundboardVolume(value: number): void {
  sfxVolume = Math.max(0, Math.min(1, value));
  if (master && ctx) {
    const target = MASTER_GAIN * sfxVolume;
    try {
      master.gain.setTargetAtTime(target, ctx.currentTime, 0.02);
    } catch {
      master.gain.value = target;
    }
  }
}

/** The served URL for a catalog sound's MP3 (the id IS the asset slug). */
function soundUrl(id: SoundReactionId): string {
  return `${BASE_URL.replace(/\/$/, '')}/sounds/${id}.mp3`;
}

/**
 * Fetch + decode a catalog clip into an {@link AudioBuffer}, caching the result. Concurrent requests for
 * the same id share one download. Returns null on any network/decode failure (a missing clip is a silent
 * no-op, never a throw). Requires a live AudioContext for `decodeAudioData`.
 */
function ensureBuffer(audioCtx: AudioContext, id: SoundReactionId): Promise<AudioBuffer | null> {
  const cached = bufferCache.get(id);
  if (cached) return Promise.resolve(cached);
  if (missingIds.has(id)) return Promise.resolve(null); // known-404: don't hammer the host
  const pending = inflight.get(id);
  if (pending) return pending;
  if (typeof fetch !== 'function') return Promise.resolve(null);

  const load = fetch(soundUrl(id))
    .then((res) => {
      if (res.ok) return res.arrayBuffer();
      // A 404 means the asset is genuinely absent (catalog/file drift) — negatively cache it so we
      // stop re-fetching. Other statuses (5xx, etc.) may be transient, so leave them retryable.
      if (res.status === 404) missingIds.add(id);
      return Promise.reject(new Error(`HTTP ${res.status}`));
    })
    .then((bytes) => audioCtx.decodeAudioData(bytes))
    .then((buffer) => {
      bufferCache.set(id, buffer);
      inflight.delete(id);
      return buffer;
    })
    .catch(() => {
      inflight.delete(id); // allow a later retry (unless 404-cached above)
      return null;
    });
  inflight.set(id, load);
  return load;
}

/**
 * Warm the decode cache for a sound WITHOUT playing it — wired to hover/focus of a soundboard button so
 * the first click is gapless. A no-op for unknown ids or when audio is unavailable.
 */
export function prefetchSoundReaction(soundId: string): void {
  if (!(SOUND_REACTION_IDS as readonly string[]).includes(soundId)) return;
  const audio = ensureContext();
  if (!audio) return;
  void ensureBuffer(audio.ctx, soundId as SoundReactionId);
}

/**
 * Play a soundboard sound for the local listener. Called for EVERYONE (incl. the sender, off the
 * server's SOUND_REACTED echo) so the moment feels shared. Resolves true if the clip actually started,
 * false if it was gated (unknown id / no audio / autoplay-suspended / decode failure / concurrency /
 * debounce).
 *
 * Safety gates (in order): valid catalog id → audio available → context running (else try-resume and
 * skip THIS play so we never blast a delayed burst) → not a same-sound double-fire → fetch+decode the
 * clip → still running & under the concurrency cap → play. All failures are silent no-ops — a sound that
 * can't play must never throw or stack up.
 */
export async function playSoundReaction(soundId: string): Promise<boolean> {
  // 1) Closed-set guard — only the catalog's ids are ever played.
  if (!(SOUND_REACTION_IDS as readonly string[]).includes(soundId)) return false;
  const id = soundId as SoundReactionId;

  const audio = ensureContext();
  if (!audio) return false;
  const { ctx: audioCtx, master: bus } = audio;

  // 2) Autoplay policy: if the context is still suspended (no gesture yet), try to resume for NEXT time
  //    and skip this play — better a silently-missed first sound than a delayed blast when it unlocks.
  if (audioCtx.state === 'suspended') {
    void audioCtx.resume();
    return false;
  }

  // 3) Same-sound debounce — coalesce an accidental double trigger of the identical sound. Done BEFORE
  //    the async load so a rapid double-fire during the cold fetch still collapses to one play.
  const now = nowMs();
  const last = lastPlayedAt.get(id);
  if (typeof last === 'number' && now - last < DEBOUNCE_MS) return false;
  lastPlayedAt.set(id, now);

  // 4) Resolve the clip (cached after first use). A failed fetch/decode is a silent no-op — and we roll
  //    back our debounce stamp so the failure doesn't swallow a quick retry (only if it's still ours).
  const buffer = await ensureBuffer(audioCtx, id);
  if (!buffer) {
    if (lastPlayedAt.get(id) === now) lastPlayedAt.delete(id);
    return false;
  }

  // The context (or autoplay state) can change across the await — re-check before committing a voice.
  if (audioCtx.state !== 'running') return false;

  // 5) Concurrency cap — drop (never queue) extra triggers so a burst can't pile into a roar.
  if (voices >= MAX_CONCURRENT) return false;

  // Per-clip source + gain (child of master) — counted so we can release the slot when the clip ends.
  const out = audioCtx.createGain();
  out.gain.value = 1;
  out.connect(bus);
  const src = audioCtx.createBufferSource();
  src.buffer = buffer;
  src.connect(out);
  voices += 1;

  src.onended = () => {
    voices = Math.max(0, voices - 1);
    try {
      out.disconnect();
    } catch {
      /* already gone */
    }
  };

  try {
    src.start();
  } catch {
    // Defensive: a scheduling hiccup must not leak a concurrency slot.
    voices = Math.max(0, voices - 1);
    try {
      out.disconnect();
    } catch {
      /* already gone */
    }
    return false;
  }

  lastPlayed = id;
  playedCount += 1;
  return true;
}

/** Dev/e2e snapshot of the soundboard audio state (drives `window.__duckSound()`). */
export function soundReactionDebug(): {
  lastPlayed: SoundReactionId | null;
  playedCount: number;
  catalog: readonly SoundReactionId[];
} {
  return { lastPlayed, playedCount, catalog: SOUND_REACTION_IDS };
}
